import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { styled } from '@mui/material/styles';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { Text } from '@famly/mf_data-display_text';
import { createBox } from '@famly/mf_layout_box';
import { Stack } from '@famly/mf_layout_stack';
import { useDataProps } from '@famly/mf_ts-utils_data-aria-props';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import { Button } from '@famly/mf_input_button';
import { DropdownButton } from '@famly/mf_input_dropdown-button';
const Image = styled(createBox({
    component: 'img',
})) `
    height: ${props => props.height};
    width: 100%;
    border-radius: 8px;

    /* Makes sure the visualization is always fully visible */
    object-fit: contain;
    object-position: center bottom;
`;
export const EmptyState = (props) => {
    var _a;
    const { isTabletPortraitAndLarger } = useBreakpoints();
    const dataProps = useDataProps(props);
    const componentWidth = React.useMemo(() => {
        switch (true) {
            case props.fullWidth:
                return '100%';
            case isTabletPortraitAndLarger:
                return '480px';
            default:
                return '100%';
        }
    }, [props.fullWidth, isTabletPortraitAndLarger]);
    const imageHeight = React.useMemo(() => {
        switch (true) {
            case hasValue(props.imageHeight):
                return props.imageHeight;
            case props.fullWidth:
                return '200px';
            case isTabletPortraitAndLarger:
                return '320px';
            default:
                return '200px';
        }
    }, [props.fullWidth, isTabletPortraitAndLarger, props.imageHeight]);
    return (_jsxs(Stack, { flexDirection: "column", alignItems: "center", width: componentWidth, ...dataProps, children: [_jsx(Image, { src: props.imageUrl || undefined, alt: (_a = props.imageAltText) !== null && _a !== void 0 ? _a : '', height: imageHeight, elevation: props.imageElevation ? 2 : undefined }), _jsxs(Stack, { flexDirection: "column", alignItems: "center", marginTop: 6, paddingLeft: 2, paddingRight: 2, children: [_jsx(Text, { variant: "h6", emphasized: true, textAlign: "center", children: props.heading }), _jsx(Text, { variant: "body", textAlign: "center", component: typeof props.body === 'string' ? 'p' : 'div', children: props.body })] }), _jsx(Buttons, { primaryButton: props.primaryButton, secondaryButton: props.secondaryButton })] }));
};
const Buttons = ({ primaryButton, secondaryButton }) => {
    const { isTabletPortraitAndLarger } = useBreakpoints();
    if (!hasValue(primaryButton) && !hasValue(secondaryButton)) {
        return null;
    }
    return isTabletPortraitAndLarger ? (_jsxs(Stack, { flexDirection: "row", alignItems: "center", gap: 3, marginTop: 8, paddingLeft: 2, paddingRight: 2, children: [hasValue(secondaryButton) ? _jsx(RenderButton, { node: secondaryButton, variant: "secondary" }) : null, hasValue(primaryButton) ? _jsx(RenderButton, { node: primaryButton, variant: "primary" }) : null] })) : (_jsxs(Stack, { flexDirection: "column", width: "100%", alignItems: "stretch", marginTop: 6, gap: 4, children: [hasValue(primaryButton) ? _jsx(RenderButton, { node: primaryButton, variant: "primary" }) : null, hasValue(secondaryButton) ? _jsx(RenderButton, { node: secondaryButton, variant: "secondary" }) : null] }));
};
// Size is deliberately not overridden: each button resolves its own from the viewport, so the
// empty state matches the rest of the product and a caller that asks for a size still gets it.
const RenderButton = ({ node, variant }) => {
    const renderedButton = React.useMemo(() => {
        const nodes = React.Children.toArray(node);
        if (nodes.length === 1) {
            const element = nodes[0];
            if (React.isValidElement(element) && element.type === Button) {
                return React.cloneElement(element, { variant });
            }
            if (React.isValidElement(element) && element.type === DropdownButton) {
                return React.cloneElement(element, { variant });
            }
        }
        return null;
    }, [node, variant]);
    const isValidButton = hasValue(renderedButton);
    React.useEffect(() => {
        if (!isValidButton) {
            // Previously this threw, which white-screened the whole route in production (Sentry
            // APP-PRODUCTION-MZ8). We log instead so the misuse stays visible (as a console error /
            // Sentry breadcrumb, and a failing consumer test via jest-fail-on-console) without
            // crashing the page. Logging from an effect keeps it to once per mount rather than on
            // every render.
            console.error('EmptyState: primaryButton/secondaryButton should be a single Button or DropdownButton. Rendering the provided node as-is.');
        }
    }, [isValidButton]);
    // The node is rendered as-is on the fallback path — without the variant override.
    return isValidButton ? renderedButton : _jsx(_Fragment, { children: node });
};
